import { MessageCircle } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import { SectionIntro } from '@/components/ui/SectionIntro';
import { useSite } from '@/features/site/SiteProvider';

/** Section E — buyer confidence: verified strengths (CMS "capabilities") and commercial terms. */
export function WhyUs({ showTerms = true }: { showTerms?: boolean } = {}) {
  const { capabilities, productionInfo } = useSite().content;

  return (
    <section aria-labelledby="why-title" className="section-y bg-surface">
      <div className="container-page grid gap-14 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-4">
          <SectionIntro id="why-title" eyebrow={capabilities.eyebrow || 'Why work with us'} title={capabilities.heading} size="md" />
          {capabilities.callout.title && (
            <div className="mt-8 flex gap-4 border-l-2 border-accent pl-5">
              <MessageCircle className="mt-0.5 h-5 w-5 shrink-0 text-accent-dark" aria-hidden />
              <div>
                <p className="font-semibold text-primary">{capabilities.callout.title}</p>
                <p className="mt-1.5 text-[14.5px] leading-relaxed text-muted">{capabilities.callout.description}</p>
              </div>
            </div>
          )}
        </div>

        <ul className="grid gap-x-10 sm:grid-cols-2 lg:col-span-8">
          {capabilities.items.map((item) => (
            <li key={item.title} className="flex gap-4 border-t border-line-strong/70 py-7">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[6px] bg-white text-primary shadow-[0_1px_2px_rgba(11,23,38,0.06)]">
                <Icon name={item.icon} className="h-[18px] w-[18px]" />
              </span>
              <div>
                <h3 className="font-display text-[17px] font-bold tracking-tight text-primary">{item.title}</h3>
                <p className="mt-1.5 text-[14.5px] leading-relaxed text-muted">{item.description}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {showTerms && productionInfo.items.length > 0 && (
        <div className="container-page mt-16">
          <div className="rounded-[8px] bg-primary px-6 py-8 text-white sm:px-10">
            <h3 className="eyebrow text-steel">{productionInfo.heading || 'Commercial terms'}</h3>
            <dl className="mt-6 grid gap-x-8 gap-y-6 sm:grid-cols-2 lg:grid-cols-5">
              {productionInfo.items.map((i) => (
                <div key={i.label} className="lg:border-l lg:border-white/15 lg:pl-6 lg:first:border-l-0 lg:first:pl-0">
                  <dt className="text-[12.5px] font-medium tracking-[0.08em] text-steel uppercase">{i.label}</dt>
                  <dd className="mt-2 font-display text-[16px] leading-snug font-bold">{i.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}
    </section>
  );
}
